import { createAsyncThunk } from "@reduxjs/toolkit";
import { ApiResponse } from "../../../types/APIResponse";
import api, { apiWithFormDataMethods } from "../../../utils/constants/api";
import { apiEndpoints } from "../../../utils/constants/apiEndpoints";
import { EstablishmentLocation } from "../../../types";

export interface DeleteEstablishmentLocation extends ApiResponse<any> {
    id: string;
}

// Fetch all establishment locations
export const fetchEstablishmentLocations = createAsyncThunk<
    EstablishmentLocation[],
    { establishmentTypeId?: string; status?: number },
    { rejectValue: string }
>(
    'establishmentLocation/fetchAll',
    async ({ establishmentTypeId, status }, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams();
            if (establishmentTypeId) params.append('establishmentTypeId', establishmentTypeId);
            if (status !== undefined) params.append('status', status.toString());

            const response = await api.get<ApiResponse<EstablishmentLocation[]>>(
                `${apiEndpoints.establishmentLocation.getAll}?${params.toString()}`
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch establishment locations');
        }
    }
);

// Fetch single establishment location
export const fetchSingleEstablishmentLocation = createAsyncThunk<
    EstablishmentLocation,
    string,
    { rejectValue: string }
>(
    'establishmentLocation/fetchSingle',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get<ApiResponse<EstablishmentLocation>>(
                `${apiEndpoints.establishmentLocation.get}/${id}`
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch establishment location');
        }
    }
);

// Add establishment location
export const addEstablishmentLocation = createAsyncThunk<
    EstablishmentLocation,
    { formData: FormData },
    { rejectValue: string }
>(
    'establishmentLocation/add',
    async ({ formData }, { rejectWithValue }) => {
        try {
            const response = await apiWithFormDataMethods.post<ApiResponse<EstablishmentLocation>>(
                apiEndpoints.establishmentLocation.create,
                formData
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to add establishment location');
        }
    }
);

// Update establishment location
export const updateEstablishmentLocation = createAsyncThunk<
    EstablishmentLocation,
    { id: string; formData: FormData },
    { rejectValue: string }
>(
    'establishmentLocation/update',
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const response = await apiWithFormDataMethods.put<ApiResponse<EstablishmentLocation>>(
                `${apiEndpoints.establishmentLocation.update}/${id}`,
                formData
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to update establishment location');
        }
    }
);

// Delete establishment location
export const deleteEstablishmentLocation = createAsyncThunk<
    DeleteEstablishmentLocation,
    { id: string },
    { rejectValue: string }
>(
    'establishmentLocation/delete',
    async ({ id }, { rejectWithValue }) => {
        try {
            const response = await api.delete<ApiResponse<DeleteEstablishmentLocation>>(
                `${apiEndpoints.establishmentLocation.delete}/${id}`
            );
            return { ...response.data, id };
        } catch (error: any) {
            // Backend returns a friendly 400 message when claims still reference the row
            // (e.g. "Cannot delete: 5 existing claim(s) reference this establishment. Disable it instead.").
            // Prefer that over the generic axios "Request failed with status code 400".
            const serverMessage = error?.response?.data?.message;
            return rejectWithValue(serverMessage || error.message || 'Failed to delete establishment location');
        }
    }
);

// Pre-check: how many active claims reference this establishment-location?
// Used by the admin UI to swap the delete confirmation into a "Disable instead" prompt
// when claimCount > 0 — before the user clicks delete and gets a 400.
export const getEstablishmentLocationClaimCount = createAsyncThunk<
    { claimCount: number; canDelete: boolean },
    { id: string },
    { rejectValue: string }
>(
    'establishmentLocation/claimCount',
    async ({ id }, { rejectWithValue }) => {
        try {
            const response: any = await api.get(
                `${apiEndpoints.establishmentLocation.claimCount}/${id}`
            );
            return response.data as { claimCount: number; canDelete: boolean };
        } catch (error: any) {
            const serverMessage = error?.response?.data?.message;
            return rejectWithValue(serverMessage || error.message || 'Failed to check claim count');
        }
    }
);

// Update order
export const updateEstablishmentLocationOrder = createAsyncThunk<
    EstablishmentLocation[],
    { orderUpdates: Array<{ id: string; index: number }> },
    { rejectValue: string }
>(
    'establishmentLocation/updateOrder',
    async ({ orderUpdates }, { rejectWithValue }) => {
        try {
            const response = await api.put<ApiResponse<EstablishmentLocation[]>>(
                apiEndpoints.establishmentLocation.updateOrder,
                { data: { orderUpdates } }
            );
            if (response?.success) {
                return response.data;
            } else {
                throw new Error(response.message || 'Failed to update order');
            }
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to update order');
        }
    }
);

// Update footer order
export const updateFooterOrder = createAsyncThunk<
    void,
    { orderUpdates: Array<{ id: string; footerOrder: number }> },
    { rejectValue: string }
>(
    'establishmentLocation/updateFooterOrder',
    async ({ orderUpdates }, { rejectWithValue }) => {
        try {
            const response = await api.put<ApiResponse<void>>(
                apiEndpoints.establishmentLocation.updateFooterOrder,
                { data: { orderUpdates } }
            );
            if (!response?.success) {
                throw new Error(response.message || 'Failed to update footer order');
            }
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to update footer order');
        }
    }
);
